import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import PropertyService from '../../services/PropertyService';
import BookingService from '../../services/BookingService';
import AuthService from '../../services/AuthService';
import { BOOKING_STATUSES, fmtPrice, getBookingStatus } from '../../constants/propertyConstants';

const fld = { display: 'flex', flexDirection: 'column', gap: 4 };
const lbl = { fontSize: 11, fontWeight: 700, color: '#8898aa', textTransform: 'uppercase' };
const inp = { padding: '10px 14px', borderRadius: 9, border: '1px solid #e3e6ec', fontSize: 14, width: '100%', boxSizing: 'border-box', outline: 'none' };
const card = { background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,.06)', padding: 22 };

export default function BookingForm() {
  const { id }   = useParams(); // property id
  const navigate = useNavigate();
  const toast    = useRef(null);
  const clientId = AuthService.getLoggedInClientId();
  const userId   = AuthService.getLoggedInUserId();

  const [prop,    setProp]    = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({
    propertyId: Number(id),
    clientId,
    salesUserId: userId,
    buyerName: '', buyerPhone: '', buyerEmail: '',
    totalAmount: '', bookingAmount: '',
    bookingDate: new Date().toISOString().slice(0, 10),
    possessionDate: '', agreementDate: '',
    bookingStatus: 'Inquiry',
    brokerName: '', brokerCommission: '',
    notes: '',
  });

  useEffect(() => {
    PropertyService.getPropertyById(id).then(setProp).catch(() => {});
    BookingService.getByPropertyId(id).then(b => setBookings(Array.isArray(b) ? b : b?.$values ?? [])).catch(() => {});
  }, [id]);

  const onChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.buyerName) { toast.current?.show({ severity: 'warn', summary: 'Required', detail: 'Buyer name is required' }); return; }
    setSaving(true);
    try {
      await BookingService.create({
        ...form,
        totalAmount:      Number(form.totalAmount)      || 0,
        bookingAmount:    Number(form.bookingAmount)    || 0,
        brokerCommission: Number(form.brokerCommission) || 0,
        possessionDate:   form.possessionDate || null,
        agreementDate:    form.agreementDate  || null,
      });
      toast.current?.show({ severity: 'success', summary: 'Booking Created', life: 2000 });
      setTimeout(() => navigate(`/property/${id}`), 1500);
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to create booking' });
    }
    setSaving(false);
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await BookingService.updateStatus(bookingId, status);
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, bookingStatus: status } : b));
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Update failed' });
    }
  };

  return (
    <div style={{ padding: 24, background: '#f4f7fa', minHeight: '100vh' }}>
      <Toast ref={toast} />

      {/* Header */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24 }}>
        <button onClick={() => navigate(`/property/${id}`)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5e72e4', fontWeight: 700, padding: 0, fontSize: 14 }}>
          <i className="fas fa-arrow-left" style={{ marginRight: 6 }} />Back
        </button>
        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1a1f36' }}>
          <i className="fas fa-file-contract" style={{ color: '#5e72e4', marginRight: 10 }} />Create Booking
        </h3>
      </div>

      {/* Property summary */}
      {prop && (
        <div style={{ ...card, background: 'linear-gradient(135deg,#5e72e4,#825ee4)', color: '#fff', marginBottom: 20 }}>
          <p style={{ margin: 0, opacity: .8, fontSize: 12 }}>Booking for</p>
          <h4 style={{ margin: '4px 0 4px', fontSize: 18 }}>{prop.propertyName}</h4>
          <p style={{ margin: 0, opacity: .8, fontSize: 13 }}>
            {prop.projectName && <span>{prop.projectName} · </span>}
            {prop.unitNumber && <span>Unit {prop.unitNumber} · </span>}
            {[prop.city, prop.state].filter(Boolean).join(', ')}
            <strong style={{ marginLeft: 16 }}>{fmtPrice(prop.price)}</strong>
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="bf-grid">
          <style>{`@media(max-width:768px){.bf-grid{grid-template-columns:1fr!important}}`}</style>

          {/* Buyer */}
          <div style={card}>
            <h6 style={{ margin: '0 0 16px', color: '#5e72e4', fontWeight: 700 }}><i className="fas fa-user" style={{ marginRight: 7 }} />Buyer Details</h6>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={fld}><label style={lbl}>Buyer Name *</label><input name="buyerName" value={form.buyerName} onChange={onChange} style={inp} required /></div>
              <div style={fld}><label style={lbl}>Phone</label><input name="buyerPhone" value={form.buyerPhone} onChange={onChange} style={inp} /></div>
              <div style={fld}><label style={lbl}>Email</label><input type="email" name="buyerEmail" value={form.buyerEmail} onChange={onChange} style={inp} /></div>
            </div>
          </div>

          {/* Financials */}
          <div style={card}>
            <h6 style={{ margin: '0 0 16px', color: '#2dce89', fontWeight: 700 }}><i className="fas fa-rupee-sign" style={{ marginRight: 7 }} />Financial</h6>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={fld}><label style={lbl}>Total Amount (₹)</label><input type="number" name="totalAmount" value={form.totalAmount} onChange={onChange} style={inp} min="0" /></div>
              <div style={fld}><label style={lbl}>Booking / Token Amount (₹)</label><input type="number" name="bookingAmount" value={form.bookingAmount} onChange={onChange} style={inp} min="0" /></div>
            </div>
          </div>

          {/* Dates */}
          <div style={card}>
            <h6 style={{ margin: '0 0 16px', color: '#fb6340', fontWeight: 700 }}><i className="fas fa-calendar" style={{ marginRight: 7 }} />Dates</h6>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={fld}><label style={lbl}>Booking Date</label><input type="date" name="bookingDate" value={form.bookingDate} onChange={onChange} style={inp} /></div>
              <div style={fld}><label style={lbl}>Agreement Date</label><input type="date" name="agreementDate" value={form.agreementDate} onChange={onChange} style={inp} /></div>
              <div style={fld}><label style={lbl}>Possession Date</label><input type="date" name="possessionDate" value={form.possessionDate} onChange={onChange} style={inp} /></div>
            </div>
          </div>

          {/* Status & Broker */}
          <div style={card}>
            <h6 style={{ margin: '0 0 16px', color: '#825ee4', fontWeight: 700 }}><i className="fas fa-handshake" style={{ marginRight: 7 }} />Status & Broker</h6>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={fld}>
                <label style={lbl}>Booking Status</label>
                <select name="bookingStatus" value={form.bookingStatus} onChange={onChange} style={inp}>
                  {BOOKING_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
              <div style={fld}><label style={lbl}>Broker Name</label><input name="brokerName" value={form.brokerName} onChange={onChange} style={inp} /></div>
              <div style={fld}><label style={lbl}>Broker Commission (₹)</label><input type="number" name="brokerCommission" value={form.brokerCommission} onChange={onChange} style={inp} min="0" /></div>
            </div>
          </div>

          {/* Notes */}
          <div style={{ ...card, gridColumn: 'span 2' }}>
            <h6 style={{ margin: '0 0 12px', color: '#8898aa', fontWeight: 700 }}><i className="fas fa-sticky-note" style={{ marginRight: 7 }} />Notes</h6>
            <textarea name="notes" value={form.notes} onChange={onChange} rows={3} placeholder="Any additional notes…" style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={() => navigate(`/property/${id}`)}
            style={{ padding: '12px 28px', borderRadius: 10, border: '1px solid #e3e6ec', background: '#fff', color: '#8898aa', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          <button type="submit" disabled={saving}
            style={{ padding: '12px 32px', background: 'linear-gradient(135deg,#5e72e4,#825ee4)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 15 }}>
            {saving ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />Saving…</> : <><i className="fas fa-save" style={{ marginRight: 8 }} />Save Booking</>}
          </button>
        </div>
      </form>

      {/* Existing bookings */}
      {bookings.length > 0 && (
        <div style={{ ...card, marginTop: 28 }}>
          <h6 style={{ margin: '0 0 16px', fontWeight: 700, color: '#1a1f36' }}>Existing Bookings for this Property</h6>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8f9fe' }}>
                  {['Buyer', 'Phone', 'Status', 'Token', 'Total', 'Date', 'Update Status'].map(h => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: '#8898aa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bookings.map(b => {
                  const bs = getBookingStatus(b.bookingStatus);
                  return (
                    <tr key={b.id} style={{ borderBottom: '1px solid #f0f2f5' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600 }}>{b.buyerName}</td>
                      <td style={{ padding: '10px 12px', color: '#8898aa' }}>{b.buyerPhone}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ background: `${bs.color}20`, color: bs.color, padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700 }}>{bs.label}</span>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#5e72e4', fontWeight: 700 }}>{fmtPrice(b.bookingAmount)}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700 }}>{fmtPrice(b.totalAmount)}</td>
                      <td style={{ padding: '10px 12px', color: '#8898aa' }}>{b.bookingDate ? new Date(b.bookingDate).toLocaleDateString('en-IN') : '—'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <select value={b.bookingStatus} onChange={e => updateBookingStatus(b.id, e.target.value)}
                          style={{ padding: '5px 8px', borderRadius: 7, border: '1px solid #e3e6ec', fontSize: 12 }}>
                          {BOOKING_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
